<template>
    <div
        ref="scroller"
        class="listener-genre"
        @scroll.passive="listenBottomOut"
    >
        <div class="listener-genre__backdrop" :style="backdropStyle"></div>
        <div class="listener-genre__glow"></div>

        <div class="listener-genre__shell">
            <div class="listener-genre__topbar">
                <button class="listener-genre__icon-button" @click="goBack">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M15 18 9 12l6-6"></path>
                    </svg>
                </button>
                <div class="listener-genre__heading">
                    <div class="listener-genre__eyebrow">{{ $tt('风格详情') }}</div>
                    <div class="listener-genre__caption">{{ heroCaption }}</div>
                </div>
            </div>

            <div class="listener-genre__hero-switch-wrap">
                <div class="listener-genre__hero-switch">
                    <button
                        :class="['listener-genre__hero-pill', !showOverviewPanel ? 'is-active' : '']"
                        @click="setDisplayMode(false)"
                    >
                        {{ $tt('封面') }}
                    </button>
                    <button
                        :class="['listener-genre__hero-pill', showOverviewPanel ? 'is-active' : '']"
                        @click="setDisplayMode(true)"
                    >
                        {{ $tt('概览') }}
                    </button>
                </div>
            </div>

            <section class="listener-genre__hero">
                <div class="listener-genre__display-stage">
                    <div v-if="!showOverviewPanel" class="listener-genre__art-stage">
                        <div class="listener-genre__disc"></div>
                        <div class="listener-genre__art-card">
                            <img
                                :src="coverImage"
                                :alt="genreTitle"
                                class="listener-genre__art-image"
                            >
                        </div>
                    </div>
                    <div v-else class="listener-genre__overview-stage">
                        <div class="listener-genre__overview-card">
                            <div class="listener-genre__overview-label">{{ $tt('库内信息') }}</div>
                            <div class="listener-genre__info-list">
                                <div class="listener-genre__info-row">
                                    <span>{{ $tt('风格名称') }}</span>
                                    <strong>{{ genreTitle }}</strong>
                                </div>
                                <div class="listener-genre__info-row">
                                    <span>{{ $tt('专辑数量') }}</span>
                                    <strong>{{ albumCount }}</strong>
                                </div>
                                <div class="listener-genre__info-row">
                                    <span>{{ $tt('歌曲数量') }}</span>
                                    <strong>{{ trackCount }}</strong>
                                </div>
                                <div class="listener-genre__info-row">
                                    <span>{{ $tt('当前加载') }}</span>
                                    <strong>{{ loadedSummary }}</strong>
                                </div>
                                <div class="listener-genre__info-row">
                                    <span>{{ $tt('代表艺术家') }}</span>
                                    <strong>{{ primaryArtist || '--' }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="listener-genre__meta">
                    <div class="listener-genre__meta-kicker">{{ $tt('Genre') }}</div>
                    <h1 class="listener-genre__title">{{ genreTitle }}</h1>
                    <p class="listener-genre__summary">{{ genreSummary }}</p>

                    <div class="listener-genre__actions">
                        <button class="listener-genre__play-button" @click="playNow">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                            </svg>
                            <span>{{ $tt('播放风格') }}</span>
                        </button>
                        <button class="listener-genre__action-button" @click="shuffleNow">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17 3h4v4h-2V6.41l-3.29 3.3-1.42-1.42L17.59 5H17V3zM3 7h5l3.29 3.29-1.42 1.42L7.17 9H3V7zm18 10v4h-4v-2h.59l-3.3-3.29 1.42-1.42 3.29 3.3V17H21zM3 15h4.17l10-10H21V3h-4v2h.59L7.17 15H3v2z"></path>
                            </svg>
                            <span>{{ $tt('随机') }}</span>
                        </button>
                    </div>
                </div>
            </section>

            <div class="listener-genre__display-switch">
                <button
                    :class="['listener-genre__display-pill', activeSection === 'albums' ? 'is-active' : '']"
                    @click="setActiveSection('albums')"
                >
                    {{ $tt('专辑') }}
                </button>
                <button
                    :class="['listener-genre__display-pill', activeSection === 'tracks' ? 'is-active' : '']"
                    @click="setActiveSection('tracks')"
                >
                    {{ $tt('歌曲') }}
                </button>
            </div>

            <div v-if="isSuperuser" class="listener-genre__admin-card">
                <div>
                    <div class="listener-genre__overview-label">{{ $tt('管理操作') }}</div>
                    <p class="listener-genre__overview-text">
                        {{ $tt('可以更新风格封面，或直接执行删除操作。') }}
                    </p>
                </div>
                <div class="listener-genre__admin-actions">
                    <button class="listener-genre__soft-button" @click="openAdminModal">
                        {{ $tt('编辑封面') }}
                    </button>
                    <button class="listener-genre__danger-button" @click="openDeleteModal">
                        {{ $tt('删除风格') }}
                    </button>
                </div>
            </div>

            <section v-if="activeSection === 'albums'" class="listener-genre__panel">
                <div class="listener-genre__panel-header">
                    <div>
                        <div class="listener-genre__panel-eyebrow">{{ $tt('风格专辑') }}</div>
                        <h2 class="listener-genre__panel-title">{{ $tt('{count} 张专辑', { count: albumCount }) }}</h2>
                    </div>
                </div>

                <ListenerAlbumList v-if="albums.length" :items="albums" />
                <div v-else-if="loadingAlbums" class="listener-genre__state">
                    <span class="loading loading-spinner loading-lg"></span>
                    <p>{{ $tt('正在加载专辑...') }}</p>
                </div>
                <div v-else class="listener-genre__empty-inline">{{ $tt('暂无专辑') }}</div>

                <div v-if="loadingAlbums && albums.length" class="listener-genre__loadmore">
                    <span class="loading loading-spinner loading-md"></span>
                </div>
            </section>

            <section v-else class="listener-genre__panel">
                <div class="listener-genre__panel-header">
                    <div>
                        <div class="listener-genre__panel-eyebrow">{{ $tt('风格歌曲') }}</div>
                        <h2 class="listener-genre__panel-title">{{ $tt('{count} 首歌曲', { count: trackCount }) }}</h2>
                    </div>
                </div>

                <ListenerTrackList
                    v-if="tracks.length"
                    :tracks="tracks"
                    index-field="index"
                />
                <div v-else-if="loadingTracks" class="listener-genre__state">
                    <span class="loading loading-spinner loading-lg"></span>
                    <p>{{ $tt('正在加载歌曲...') }}</p>
                </div>
                <div v-else class="listener-genre__empty-inline">{{ $tt('暂无歌曲') }}</div>

                <div v-if="loadingTracks && tracks.length" class="listener-genre__loadmore">
                    <span class="loading loading-spinner loading-md"></span>
                </div>
            </section>
        </div>

        <dialog ref="adminModal" class="modal">
            <div class="modal-box listener-genre__modal-box">
                <div class="listener-genre__modal-header">
                    <div>
                        <div class="listener-genre__panel-eyebrow">{{ $tt('风格编辑') }}</div>
                        <h3 class="listener-genre__modal-title">{{ genreTitle }}</h3>
                    </div>
                </div>

                <div class="listener-genre__modal-section">
                    <div class="listener-genre__modal-label">{{ $tt('当前封面') }}</div>
                    <div class="listener-genre__upload-card">
                        <div class="listener-genre__upload-preview">
                            <img :src="adminPreviewImage" :alt="genreTitle">
                            <button
                                v-if="genreImagePreview"
                                class="listener-genre__upload-clear"
                                type="button"
                                @click="removeGenreImage"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M18 6L6 18"></path>
                                    <path d="M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                        <div class="listener-genre__upload-actions">
                            <button class="listener-genre__soft-button" type="button" @click="triggerGenreImageReplacement">
                                {{ $tt('上传图片') }}
                            </button>
                            <button
                                v-if="albums.length"
                                class="listener-genre__soft-button"
                                type="button"
                                @click="handleCopy('genre_img', albums[0].image)"
                            >
                                {{ $tt('使用首张专辑封面') }}
                            </button>
                        </div>
                        <div v-if="isUploadingGenreImage" class="listener-genre__upload-state">
                            <span class="loading loading-spinner loading-md"></span>
                            <span>{{ $tt('上传中...') }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="albums.length" class="listener-genre__modal-section">
                    <div class="listener-genre__modal-label">{{ $tt('推荐封面') }}</div>
                    <div class="listener-genre__cover-grid">
                        <button
                            v-for="item in albums.slice(0, 6)"
                            :key="item.id"
                            class="listener-genre__cover-option"
                            type="button"
                            @click="handleCopy('genre_img', item.image)"
                        >
                            <img :src="item.image || '/static/dist/img/album_null.jpg'" :alt="item.name">
                            <span>{{ item.name }}</span>
                        </button>
                    </div>
                </div>

                <div class="listener-genre__modal-footer">
                    <button class="listener-genre__play-button listener-genre__play-button--modal" type="button" @click="handleSave">
                        {{ $tt('保存修改') }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>{{ $tt('关闭') }}</button>
            </form>
        </dialog>

        <dialog ref="deleteModal" class="modal">
            <div class="modal-box listener-genre__modal-box">
                <div class="listener-genre__modal-header">
                    <div>
                        <div class="listener-genre__panel-eyebrow">{{ $tt('删除风格') }}</div>
                        <h3 class="listener-genre__modal-title">{{ genreTitle }}</h3>
                    </div>
                </div>
                <div class="listener-genre__delete-copy">
                    <p>{{ $tt('删除音乐: 将风格从音乐收藏中移除，同时删除该风格下的音乐文件。') }}</p>
                    <p>{{ $tt('删除收藏: 将风格从音乐收藏中移除，但并不会删除音乐文件。') }}</p>
                </div>
                <div class="listener-genre__admin-actions">
                    <button class="listener-genre__danger-button" type="button" @click="handleDelete('delete_file')">
                        {{ $tt('删除音乐') }}
                    </button>
                    <button class="listener-genre__soft-button" type="button" @click="handleDelete('delete_track')">
                        {{ $tt('删除收藏') }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>{{ $tt('关闭') }}</button>
            </form>
        </dialog>

        <button
            v-if="curScrollTop > 700"
            class="listener-genre__to-top"
            @click="scrollToTop"
        >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 19V5"></path>
                <path d="M5 12l7-7 7 7"></path>
            </svg>
        </button>
    </div>
</template>

<script>
import ListenerAlbumList from '@/views/library/album/ListenerAlbumList.vue'
import ListenerTrackList from '@/views/library/track/ListenerTrackList.vue'
import { mapGetters } from 'vuex'

const PAGE_SIZE = 24

export default {
    name: 'ListenerGenreDetails',
    components: {
        ListenerAlbumList,
        ListenerTrackList
    },
    data() {
        return {
            id: this.$route.params.id,
            activeSection: 'tracks',
            showOverviewPanel: false,
            genreInfo: null,
            currentGenreImage: '',
            genreImagePreview: '',
            albums: [],
            tracks: [],
            loadingAlbums: false,
            loadingTracks: false,
            albumOffset: 0,
            trackOffset: 0,
            hasMoreAlbums: true,
            hasMoreTracks: true,
            curScrollTop: 0,
            formData: {
                genre_img: ''
            },
            uploadUrl: '/apimt/upload_image/',
            isUploadingGenreImage: false
        }
    },
    computed: {
        ...mapGetters({
            isSuperuser: 'getIsSuperuser'
        }),
        genreTitle() {
            return this.genreInfo?.name || this.id
        },
        albumCount() {
            return Number(this.genreInfo?.albumCount || 0) || this.albums.length
        },
        trackCount() {
            return Number(this.genreInfo?.trackCount || 0) || this.tracks.length
        },
        coverImage() {
            return this.genreImagePreview
                || this.currentGenreImage
                || (this.albums[0] && this.albums[0].image)
                || '/static/dist/img/album_null.jpg'
        },
        adminPreviewImage() {
            return this.genreImagePreview || this.currentGenreImage || this.coverImage
        },
        backdropStyle() {
            return {
                backgroundImage: `url('${this.coverImage}')`
            }
        },
        primaryArtist() {
            const firstAlbum = this.albums.find(item => item.artist)
            return firstAlbum ? firstAlbum.artist : ''
        },
        heroCaption() {
            return this.$tt('genre.details.caption', {
                albumCount: this.albumCount,
                trackCount: this.trackCount
            }) === 'genre.details.caption'
                ? this.$tt('{albumCount} 张专辑 · {trackCount} 首歌曲', {
                    albumCount: this.albumCount,
                    trackCount: this.trackCount
                })
                : this.$tt('genre.details.caption', {
                    albumCount: this.albumCount,
                    trackCount: this.trackCount
                })
        },
        loadedSummary() {
            return this.$tt('{albumCount} / {trackCount}', {
                albumCount: this.albums.length,
                trackCount: this.tracks.length
            })
        },
        genreSummary() {
            if (!this.albumCount && !this.trackCount) {
                return this.$tt('这个风格还没有可播放内容。')
            }
            if (this.albumCount && this.trackCount) {
                return this.$tt('这里已经收集了 {albumCount} 张专辑和 {trackCount} 首歌曲，适合从同一种气质一路听下去。', {
                    albumCount: this.albumCount,
                    trackCount: this.trackCount
                })
            }
            if (this.trackCount) {
                return this.$tt('这里已经整理出 {trackCount} 首歌曲，适合直接开播。', {
                    trackCount: this.trackCount
                })
            }
            return this.$tt('这里已经整理出 {albumCount} 张专辑，可以从封面慢慢挑。', {
                albumCount: this.albumCount
            })
        }
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            async handler(value) {
                this.id = value
                await this.initializePage()
            }
        }
    },
    activated() {
        this.$nextTick(() => {
            if (this.$refs.scroller) {
                this.$refs.scroller.scrollTop = this.curScrollTop || 0
            }
        })
    },
    methods: {
        async initializePage() {
            this.activeSection = 'tracks'
            this.showOverviewPanel = false
            this.genreInfo = null
            this.currentGenreImage = ''
            this.genreImagePreview = ''
            this.formData.genre_img = ''
            this.albums = []
            this.tracks = []
            this.albumOffset = 0
            this.trackOffset = 0
            this.hasMoreAlbums = true
            this.hasMoreTracks = true
            this.curScrollTop = 0

            if (this.$refs.scroller) {
                this.$refs.scroller.scrollTop = 0
            }

            await Promise.all([
                this.loadGenreMeta(),
                this.loadMore('albums'),
                this.loadMore('tracks')
            ])
        },
        async loadGenreMeta() {
            try {
                const items = await this.$api.subsonic.getGenres('most-tracks')
                const target = items.find(item => String(item.id) === String(this.id))
                if (target) {
                    this.genreInfo = target
                    this.currentGenreImage = target.image || ''
                }
            } catch (error) {
                console.log(error)
            }
        },
        async loadMore(section) {
            if (section === 'albums') {
                if (this.loadingAlbums || !this.hasMoreAlbums) {
                    return
                }
                this.loadingAlbums = true
                try {
                    const items = await this.$api.subsonic.getAlbumsByGenre(this.id, PAGE_SIZE, this.albumOffset)
                    this.albums.push(...items)
                    this.albumOffset += items.length
                    this.hasMoreAlbums = items.length === PAGE_SIZE
                } finally {
                    this.loadingAlbums = false
                }
                return
            }

            if (this.loadingTracks || !this.hasMoreTracks) {
                return
            }
            this.loadingTracks = true
            try {
                const items = await this.$api.subsonic.getTracksByGenre(this.id, PAGE_SIZE, this.trackOffset)
                this.tracks.push(...items)
                this.trackOffset += items.length
                this.hasMoreTracks = items.length === PAGE_SIZE
            } finally {
                this.loadingTracks = false
            }
        },
        async ensureTracksForPlayback() {
            if (this.tracks.length >= Math.min(this.trackCount || PAGE_SIZE, PAGE_SIZE)) {
                return this.tracks
            }
            const items = await this.$api.subsonic.getTracksByGenre(this.id, 100, 0)
            if (!this.tracks.length) {
                this.tracks = items
                this.trackOffset = items.length
                this.hasMoreTracks = items.length === 100
            }
            return items
        },
        async playNow() {
            const tracks = await this.ensureTracksForPlayback()
            if (!tracks.length) {
                return this.$myMsg.notify({
                    title: this.$tt('暂无歌曲'),
                    type: 'warning',
                    time: 2000,
                })
            }
            return this.$store.dispatch('playTrackList', {
                tracks
            })
        },
        async shuffleNow() {
            const tracks = await this.ensureTracksForPlayback()
            if (!tracks.length) {
                return this.$myMsg.notify({
                    title: this.$tt('暂无歌曲'),
                    type: 'warning',
                    time: 2000,
                })
            }
            return this.$store.dispatch('player/shuffleNow', {
                tracks
            })
        },
        setDisplayMode(isOverview) {
            this.showOverviewPanel = isOverview
        },
        async setActiveSection(value) {
            this.activeSection = value
            if (value === 'albums' && !this.albums.length && this.hasMoreAlbums) {
                await this.loadMore('albums')
            }
            if (value === 'tracks' && !this.tracks.length && this.hasMoreTracks) {
                await this.loadMore('tracks')
            }
        },
        listenBottomOut() {
            if (!this.$refs.scroller) {
                return
            }
            const scroller = this.$refs.scroller
            const scrollTop = scroller.scrollTop
            const scrollHeight = scroller.scrollHeight
            const clientHeight = scroller.clientHeight
            this.curScrollTop = scrollTop

            if (scrollTop + clientHeight < scrollHeight - 80) {
                return
            }

            if (this.activeSection === 'albums') {
                this.loadMore('albums')
            } else if (this.activeSection === 'tracks') {
                this.loadMore('tracks')
            }
        },
        scrollToTop() {
            if (this.$refs.scroller) {
                this.$refs.scroller.scrollTo({ top: 0, behavior: 'smooth' })
            }
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back()
                return
            }
            this.$router.push({ path: '/genres/a-z/' })
        },
        openAdminModal() {
            if (this.$refs.adminModal) {
                this.$refs.adminModal.showModal()
            }
        },
        openDeleteModal() {
            if (this.$refs.deleteModal) {
                this.$refs.deleteModal.showModal()
            }
        },
        async handleDelete(mode) {
            const res = await this.$api.Task.deleteGenre({ id: this.id, mode })
            if (res.result) {
                if (this.$refs.deleteModal) {
                    this.$refs.deleteModal.close()
                }
                this.$myMsg.notify({
                    title: this.$tt('删除成功！'),
                    type: 'success',
                    time: 2000,
                })
                this.$router.push({ path: '/genres/a-z/' })
                return
            }
            this.$myMsg.notify({
                title: this.$tt('删除失败！'),
                type: 'error',
                time: 2000,
            })
        },
        handleRes(response) {
            if (response.result) {
                this.formData.genre_img = response.data
                return true
            }
            return false
        },
        async handleSave() {
            const res = await this.$api.Task.postGenre({
                id: this.id,
                genre_img: this.formData.genre_img,
            })
            if (res.result) {
                if (this.genreImagePreview) {
                    this.currentGenreImage = this.genreImagePreview
                }
                this.$myMsg.notify({
                    title: this.$tt('修改成功！'),
                    type: 'success',
                    time: 2000,
                })
                if (this.$refs.adminModal) {
                    this.$refs.adminModal.close()
                }
                return
            }
            this.$myMsg.notify({
                title: this.$tt('修改失败！'),
                type: 'error',
                time: 2000,
            })
        },
        handleCopy(key, value) {
            if (key !== 'genre_img') {
                return
            }
            this.formData.genre_img = value
            this.genreImagePreview = value
        },
        removeGenreImage() {
            this.genreImagePreview = ''
            this.formData.genre_img = ''
            if (this.$refs.genreImageInput) {
                this.$refs.genreImageInput.value = ''
            }
        },
        triggerGenreImageReplacement() {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/*'
            input.style.display = 'none'
            input.onchange = (event) => {
                this.handleGenreImageChange(event)
                if (input.parentNode) {
                    input.parentNode.removeChild(input)
                }
            }
            document.body.appendChild(input)
            input.click()
        },
        handleGenreImageChange(event) {
            const file = event.target.files && event.target.files[0]
            if (!file) {
                return
            }

            if (!file.type.startsWith('image/')) {
                this.$myMsg.notify({
                    title: this.$tt('文件类型错误'),
                    content: this.$tt('请选择图片文件'),
                    type: 'error',
                    time: 2000,
                })
                return
            }

            if (file.size > 5 * 1024 * 1024) {
                this.$myMsg.notify({
                    title: this.$tt('文件过大'),
                    content: this.$tt('图片大小不能超过5MB'),
                    type: 'error',
                    time: 2000,
                })
                return
            }

            const reader = new FileReader()
            reader.onload = (e) => {
                this.genreImagePreview = e.target.result
            }
            reader.readAsDataURL(file)
            this.uploadImage(file)
        },
        uploadImage(file) {
            const formData = new FormData()
            formData.append('upload_file', file)
            this.isUploadingGenreImage = true

            const xhr = new XMLHttpRequest()
            xhr.open('POST', this.uploadUrl, true)
            xhr.setRequestHeader('X-CSRFToken', this.getCookie('django_vue_cli_csrftoken'))
            xhr.setRequestHeader('AUTHORIZATION', this.getCookie('AUTHORIZATION'))
            xhr.withCredentials = true

            xhr.onload = () => {
                this.isUploadingGenreImage = false
                if (xhr.status !== 200) {
                    this.$myMsg.notify({
                        title: this.$tt('上传失败'),
                        content: this.$tt('HTTP错误: {status}', { status: xhr.status }),
                        type: 'error',
                        time: 2000,
                    })
                    return
                }

                try {
                    const response = JSON.parse(xhr.responseText)
                    if (this.handleRes(response)) {
                        this.$myMsg.notify({
                            title: this.$tt('上传成功'),
                            type: 'success',
                            time: 2000,
                        })
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('上传失败'),
                            content: response.message || this.$tt('未知错误'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                } catch (error) {
                    console.log(error)
                    this.$myMsg.notify({
                        title: this.$tt('上传失败'),
                        content: this.$tt('服务器响应格式错误'),
                        type: 'error',
                        time: 2000,
                    })
                }
            }

            xhr.onerror = () => {
                this.isUploadingGenreImage = false
                this.$myMsg.notify({
                    title: this.$tt('上传失败'),
                    content: this.$tt('网络错误'),
                    type: 'error',
                    time: 2000,
                })
            }

            xhr.send(formData)
        }
    }
}
</script>

<style scoped>
.listener-genre {
    --display-stage-size: min(19rem, 78vw, 34dvh);
    position: relative;
    height: calc(100dvh - 12.25rem);
    box-sizing: border-box;
    padding: 0.85rem 0.9rem 2rem;
    overflow-y: auto;
    overflow-x: hidden;
    color: #0f172a;
    background:
        radial-gradient(circle at top left, rgba(191, 219, 254, 0.4), transparent 32%),
        linear-gradient(180deg, #f7f6f3 0%, #eef4ff 52%, #f8fafc 100%);
}

.listener-genre__backdrop,
.listener-genre__glow {
    position: absolute;
    pointer-events: none;
}

.listener-genre__backdrop {
    inset: 0;
    background-size: cover;
    background-position: center;
    opacity: 0.14;
    filter: blur(36px);
    transform: scale(1.08);
}

.listener-genre__glow {
    top: 4rem;
    right: -3rem;
    width: 12rem;
    height: 12rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.46);
    filter: blur(10px);
}

.listener-genre__shell {
    position: relative;
    z-index: 1;
}

.listener-genre__topbar,
.listener-genre__heading,
.listener-genre__header-copy,
.listener-genre__chips,
.listener-genre__actions,
.listener-genre__display-switch,
.listener-genre__panel-header,
.listener-genre__admin-actions,
.listener-genre__subsection-header,
.listener-genre__upload-actions,
.listener-genre__modal-footer {
    display: flex;
}

.listener-genre__topbar,
.listener-genre__panel-header,
.listener-genre__subsection-header {
    align-items: center;
    justify-content: space-between;
}

.listener-genre__topbar {
    gap: 0.9rem;
}

.listener-genre__heading {
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 0.15rem;
}

.listener-genre__eyebrow,
.listener-genre__panel-eyebrow,
.listener-genre__meta-kicker,
.listener-genre__overview-label,
.listener-genre__modal-label {
    font-size: 0.82rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-genre__caption {
    font-size: 0.83rem;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-genre__icon-button,
.listener-genre__to-top,
.listener-genre__upload-clear {
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.listener-genre__icon-button {
    width: 2.8rem;
    height: 2.8rem;
    background: rgba(255, 255, 255, 0.84);
    color: #111827;
    box-shadow: 0 14px 28px rgba(148, 163, 184, 0.14);
    flex-shrink: 0;
}

.listener-genre__icon-button svg,
.listener-genre__to-top svg,
.listener-genre__upload-clear svg {
    width: 1.05rem;
    height: 1.05rem;
}

.listener-genre__hero {
    display: flex;
    flex-direction: column;
    padding: 0;
}

.listener-genre__hero-switch-wrap {
    margin: 1.15rem auto 0;
    width: 100%;
    display: flex;
    justify-content: center;
}

.listener-genre__hero-switch {
    padding: 0.28rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
    width: fit-content;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.28rem;
}

.listener-genre__hero-pill {
    min-width: 4.7rem;
    height: 2.2rem;
    padding: 0 1rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 700;
}

.listener-genre__hero-pill.is-active {
    background: rgba(17, 24, 39, 0.96);
    color: #fff;
}

.listener-genre__display-stage {
    height: var(--display-stage-size);
    margin-top: 1.25rem;
    margin-bottom: 1.45rem;
    display: flex;
    align-items: stretch;
}

.listener-genre__art-stage {
    position: relative;
    margin: 0 auto;
    width: var(--display-stage-size);
    height: var(--display-stage-size);
    max-width: 100%;
    align-self: center;
}

.listener-genre__overview-stage {
    width: 100%;
    height: 100%;
    display: grid;
    align-content: start;
    gap: 0.9rem;
    overflow-y: auto;
    padding-right: 0.1rem;
}

.listener-genre__disc {
    position: absolute;
    right: -1.5rem;
    top: 1.7rem;
    width: 72%;
    height: 72%;
    border-radius: 999px;
    background:
        radial-gradient(circle at center, #0f172a 0 10%, #111827 10% 15%, #020617 15% 52%, #111827 52% 60%, #020617 60% 100%);
    box-shadow: 0 22px 50px rgba(15, 23, 42, 0.22);
}

.listener-genre__art-card {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
    padding: 0.8rem;
    border-radius: 1.7rem;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.92), rgba(226, 232, 240, 0.68));
    backdrop-filter: blur(20px);
    box-shadow: 0 28px 70px rgba(99, 102, 241, 0.18);
}

.listener-genre__art-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1.45rem;
}

.listener-genre__title {
    margin-top: 0.2rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    letter-spacing: -0.04em;
    color: #111827;
}

.listener-genre__summary {
    margin-top: 0.75rem;
    font-size: 0.92rem;
    line-height: 1.6;
    color: #475569;
}

.listener-genre__chip,
.listener-genre__info-row span,
.listener-genre__delete-copy,
.listener-genre__cover-option span {
    color: #64748b;
}

.listener-genre__info-row strong {
    display: block;
    margin-top: 0.25rem;
    font-size: 1.05rem;
    font-weight: 800;
    color: #111827;
}

.listener-genre__chips,
.listener-genre__actions,
.listener-genre__display-switch,
.listener-genre__admin-actions,
.listener-genre__upload-actions {
    gap: 0.7rem;
    flex-wrap: wrap;
}

.listener-genre__chips {
    margin-top: 1rem;
}

.listener-genre__chip {
    padding: 0.48rem 0.82rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.85);
    font-size: 0.76rem;
    font-weight: 700;
    box-shadow: 0 10px 22px rgba(148, 163, 184, 0.08);
}

.listener-genre__chip--primary {
    background: rgba(17, 24, 39, 0.94);
    color: #fff;
}

.listener-genre__actions {
    margin-top: 1.1rem;
}

.listener-genre__play-button,
.listener-genre__action-button,
.listener-genre__soft-button,
.listener-genre__danger-button,
.listener-genre__ghost-link {
    border: 0;
    font-weight: 700;
}

.listener-genre__play-button,
.listener-genre__action-button,
.listener-genre__soft-button,
.listener-genre__danger-button {
    min-height: 3rem;
    border-radius: 999px;
    padding: 0 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.listener-genre__play-button {
    background: rgba(17, 24, 39, 0.95);
    color: #fff;
    box-shadow: 0 18px 36px rgba(15, 23, 42, 0.18);
}

.listener-genre__play-button svg,
.listener-genre__action-button svg {
    width: 1rem;
    height: 1rem;
}

.listener-genre__action-button,
.listener-genre__soft-button {
    background: rgba(255, 255, 255, 0.88);
    color: #111827;
    box-shadow: 0 12px 24px rgba(148, 163, 184, 0.1);
}

.listener-genre__danger-button {
    background: rgba(239, 68, 68, 0.12);
    color: #b91c1c;
}

.listener-genre__display-switch {
    margin: 1rem auto 0;
    padding: 0.35rem;
    width: fit-content;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 12px 24px rgba(148, 163, 184, 0.08);
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.listener-genre__display-pill {
    min-width: 4.7rem;
    min-height: 2.7rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    font-size: 0.84rem;
    font-weight: 700;
}

.listener-genre__display-pill.is-active {
    background: rgba(17, 24, 39, 0.96);
    color: #fff;
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.16);
}

.listener-genre__panel,
.listener-genre__admin-card {
    margin-top: 1rem;
    border-radius: 1.8rem;
}

.listener-genre__panel {
    padding: 1rem;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
}

.listener-genre__panel-title,
.listener-genre__subsection-title,
.listener-genre__modal-title {
    margin-top: 0.18rem;
    font-size: 1.15rem;
    font-weight: 800;
    color: #111827;
}

.listener-genre__overview-card,
.listener-genre__admin-card {
    padding: 1rem;
}

.listener-genre__overview-text {
    margin-top: 0.55rem;
    font-size: 0.88rem;
    line-height: 1.65;
    color: #475569;
}

.listener-genre__info-list {
    margin-top: 0.7rem;
    display: grid;
    gap: 0.7rem;
}

.listener-genre__info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.listener-genre__subsection {
    margin-top: 1.15rem;
}

.listener-genre__ghost-link {
    padding: 0;
    background: transparent;
    color: #111827;
    font-size: 0.84rem;
}

.listener-genre__empty-inline,
.listener-genre__state,
.listener-genre__inline-state,
.listener-genre__loadmore,
.listener-genre__upload-state {
    display: flex;
    align-items: center;
    justify-content: center;
}

.listener-genre__empty-inline,
.listener-genre__state {
    min-height: 8rem;
    color: #64748b;
}

.listener-genre__inline-state,
.listener-genre__loadmore,
.listener-genre__upload-state {
    gap: 0.5rem;
}

.listener-genre__loadmore {
    padding: 1rem 0 0.35rem;
}

.listener-genre__modal-box {
    border-radius: 1.8rem;
    background: linear-gradient(180deg, #f8fafc 0%, #f7f6f3 100%);
}

.listener-genre__modal-section {
    margin-top: 1rem;
}

.listener-genre__upload-card {
    margin-top: 0.7rem;
    padding: 0.95rem;
    border-radius: 1.35rem;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 12px 24px rgba(148, 163, 184, 0.1);
}

.listener-genre__upload-preview {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 1.2rem;
    overflow: hidden;
}

.listener-genre__upload-preview img,
.listener-genre__cover-option img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.listener-genre__upload-clear {
    position: absolute;
    top: 0.7rem;
    right: 0.7rem;
    width: 2rem;
    height: 2rem;
    background: rgba(17, 24, 39, 0.88);
    color: #fff;
}

.listener-genre__upload-actions,
.listener-genre__upload-state {
    margin-top: 0.8rem;
}

.listener-genre__cover-grid {
    margin-top: 0.7rem;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.listener-genre__cover-option {
    border: 0;
    padding: 0.5rem;
    border-radius: 1.2rem;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 10px 22px rgba(148, 163, 184, 0.1);
    text-align: left;
}

.listener-genre__cover-option img {
    aspect-ratio: 1 / 1;
    border-radius: 0.95rem;
}

.listener-genre__cover-option span {
    display: block;
    margin-top: 0.45rem;
    font-size: 0.76rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-genre__delete-copy {
    margin-top: 0.9rem;
    display: grid;
    gap: 0.55rem;
    font-size: 0.88rem;
    line-height: 1.6;
}

.listener-genre__play-button--modal {
    width: 100%;
}

.listener-genre__to-top {
    position: fixed;
    right: 1rem;
    bottom: 7rem;
    width: 3rem;
    height: 3rem;
    background: rgba(17, 24, 39, 0.92);
    color: #fff;
    box-shadow: 0 18px 36px rgba(15, 23, 42, 0.2);
}

@media (max-width: 420px) {
    .listener-genre {
        padding: 0.8rem 0.78rem 2rem;
    }

    .listener-genre__hero,
    .listener-genre__panel {
        padding-left: 0.9rem;
        padding-right: 0.9rem;
    }

    .listener-genre__cover-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
